import {
  PermissionContext,
  permissionsOperations,
} from '@ardoq/access-control';
import {
  APIDiscoverViewpointAttributes,
  ArdoqId,
  PrivilegeLabel,
} from '@ardoq/api-types';
import { FeatureContext, Features, featureOperations } from '@ardoq/features';

const canCreateViewpoint = (permissionContext: PermissionContext): boolean =>
  permissionsOperations.hasPrivilege(
    permissionContext,
    PrivilegeLabel.CREATE_VIEWPOINT
  ) &&
  permissionsOperations.hasPrivilege(
    permissionContext,
    PrivilegeLabel.ACCESS_DISCOVER
  );

const canAdminViewpoint = (
  permissionContext: PermissionContext,
  viewpointId: ArdoqId
): boolean =>
  permissionsOperations.hasWriterRolePlus(permissionContext) &&
  (permissionsOperations.hasPrivilege(
    permissionContext,
    PrivilegeLabel.GLOBAL_DATA_ACCESS
  ) ||
    permissionsOperations.hasAdminAccessToResource(
      permissionContext,
      viewpointId
    ));

const canEditViewpoint = (
  permissionContext: PermissionContext,
  viewpointId: ArdoqId
): boolean =>
  permissionsOperations.hasWriterRolePlus(permissionContext) &&
  (permissionsOperations.hasPrivilege(
    permissionContext,
    PrivilegeLabel.GLOBAL_DATA_ACCESS
  ) ||
    permissionsOperations.hasEditAccessToResource(
      permissionContext,
      viewpointId
    ) ||
    canAdminViewpoint(permissionContext, viewpointId));

const canReadViewpoint = (
  permissionContext: PermissionContext,
  viewpointId: ArdoqId
): boolean =>
  permissionsOperations.hasPrivilege(
    permissionContext,
    PrivilegeLabel.GLOBAL_DATA_ACCESS
  ) ||
  permissionsOperations.hasReadAccessToResource(
    permissionContext,
    viewpointId
  ) ||
  canEditViewpoint(permissionContext, viewpointId);

const canCopyViewpoint = (permissionContext: PermissionContext): boolean =>
  canCreateViewpoint(permissionContext) &&
  permissionsOperations.hasPrivilege(
    permissionContext,
    PrivilegeLabel.GLOBAL_DATA_ACCESS
  );

const getDiscoverViewpointOverViewHidden = (
  permissionContext: PermissionContext,
  featureContext: FeatureContext,
  viewpoints?: APIDiscoverViewpointAttributes[] | null
) => {
  if (
    featureOperations.hasFeature(
      featureContext,
      Features.HIDE_LEGACY_DISCOVER_AND_VIEWPOINTS
    )
  )
    return true;
  const hasViewpoints = viewpoints && viewpoints.length;
  const hasSomeViewpointEditAccess =
    hasViewpoints &&
    viewpoints.some(viewpoint =>
      canEditViewpoint(permissionContext, viewpoint._id)
    );

  const isCanCreateViewpoint = canCreateViewpoint(permissionContext);
  return !isCanCreateViewpoint && !hasSomeViewpointEditAccess;
};

export const viewpointAccessControlInterface = {
  canCreateViewpoint,
  canAdminViewpoint,
  canEditViewpoint,
  canReadViewpoint,
  canCopyViewpoint,
  getDiscoverViewpointOverViewHidden,
};
